import styles from './file-receiver-success-message.module.css'

import React from 'react'

import mixpanelClient from '@disco/api/mixpanel'
import {
  DISCO_SITE_SIGNUP_FROM_BRIEF_SUBMISSION_URL,
  DISCO_SITE_SIGNUP_FROM_PUBLIC_INBOX_URL,
} from '@disco/constants/external-links'
import businessCookie from '@disco/utils/business-cookie'

import { Box, Button, Flex, Text } from 'components/ds'

import DiscoIcon from './disco-icon.svg'
import { Trans } from '@disco/i18n/trans'
import { useTranslation } from '@disco/i18n/use-translation'

export const FileReceiverSuccessMessage = ({
  isBrief = false,
  playlistName,
}: {
  isBrief?: boolean
  playlistName?: string
}) => {
  const { t } = useTranslation(['inboxes', 'briefs'])
  const businessName = window.business_data?.name ?? ''
  const businessImageUrl = window.business_data?.image ?? ''

  const firstExistingBusiness = businessCookie()[0]

  const signupUrl = isBrief
    ? DISCO_SITE_SIGNUP_FROM_BRIEF_SUBMISSION_URL
    : DISCO_SITE_SIGNUP_FROM_PUBLIC_INBOX_URL
  const utmMedium = isBrief ? 'brief_submission' : 'public_inbox'

  function handleDiscoLinkClick(eventName: string, copy: string) {
    mixpanelClient.sendPureEvent(eventName, {
      Type: 'Try DISCO',
      Location: 'Upload',
      Destination: signupUrl,
      Copy: copy,
      'UTM Source': 'disco_playlist',
      'UTM Medium': utmMedium,
    })
  }

  return (
    <Flex
      direction="column"
      gap="4"
      align="center"
      flexGrow="1"
      px="5"
      className={styles['file-receiver-success-message-container']}
    >
      {businessImageUrl && (
        <img
          src={businessImageUrl}
          alt={t('inboxes:businessLogoAlt', '{{businessName}} logo', {
            businessName,
          })}
          className={styles['file-receiver-success-message-image']}
        />
      )}
      <Text
        weight="medium"
        className={styles['file-receiver-success-message-title']}
        align="center"
      >
        {isBrief ? (
          <>
            {/* Using unicode checkmark icon according to figma design */}
            <Text color="green">✓⃝</Text>
            <span>
              {t(
                'briefs:thankYouForBrief',
                'Thank you for responding to this brief!',
              )}
            </span>
            {businessName && (
              <span>
                {' '}
                {t(
                  'inboxes:businessNotified',
                  '{{businessName}} has been notified.',
                  { businessName },
                )}
              </span>
            )}
          </>
        ) : (
          <>
            {/* Using unicode checkmark icon according to figma design */}
            <Text color="green">✓⃝</Text>
            {/* One sentence per key: the CLI had split this into "Your",
                "playlist has been sent" and "to {{businessName}}", which no
                locale with different word order can reassemble. */}
            <span>
              {businessName ? (
                <Trans
                  t={t}
                  i18nKey="inboxes:playlistSentToBusiness"
                  defaults="Your <name>{{playlistName}}</name> playlist has been sent to {{businessName}}"
                  values={{ playlistName, businessName }}
                  components={{ name: <Text weight="bold" /> }}
                />
              ) : (
                <Trans
                  t={t}
                  i18nKey="inboxes:playlistSent"
                  defaults="Your <name>{{playlistName}}</name> playlist has been sent"
                  values={{ playlistName }}
                  components={{ name: <Text weight="bold" /> }}
                />
              )}
            </span>
          </>
        )}
      </Text>
      {!firstExistingBusiness && (
        <Text
          className={styles['file-receiver-success-message-subtitle']}
          align="center"
        >
          {t(
            'inboxes:poweredByDisco',
            'Powered by DISCO, the all-in-one music management platform. Helping music go further, faster.',
          )}{' '}
          <a
            href={signupUrl}
            rel="noopener"
            target="_blank"
            className={styles['file-receiver-success-message-link']}
            onClick={() => handleDiscoLinkClick('Click link', 'Learn more')}
          >
            {t('inboxes:learnMore', 'Learn more')}
          </a>
        </Text>
      )}
      <Box mt="18px">
        {firstExistingBusiness ? (
          <Button asChild size="4">
            <a href={firstExistingBusiness.url}>
              {t('inboxes:backToMyDisco', 'Back to my DISCO')}
            </a>
          </Button>
        ) : (
          <Button asChild size="4">
            <a
              href={signupUrl}
              rel="noopener"
              target="_blank"
              onClick={() =>
                handleDiscoLinkClick('Click button', 'Try DISCO for free')
              }
            >
              <DiscoIcon
                className={styles['file-receiver-success-message-button-icon']}
              />
              {t('inboxes:tryDiscoForFree', 'Try DISCO for free')}
            </a>
          </Button>
        )}
      </Box>
    </Flex>
  )
}
